import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "../../../supabase/supabase";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Loader2,
  RotateCcw,
  Save,
  Sparkles,
  X,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PromptWithVersions } from "@/types/prompt";
import InlineDiff from "./InlineDiff";

export default function PromptAIEdit() {
  const { id } = useParams<{ id: string }>();
  const [prompt, setPrompt] = useState<PromptWithVersions | null>(null);
  const [currentContent, setCurrentContent] = useState("");
  const [generatedContent, setGeneratedContent] = useState("");
  const [feedback, setFeedback] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDiff, setShowDiff] = useState(false);
  const [chatHistory, setChatHistory] = useState<
    Array<{ role: string; content: string }>
  >([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchPrompt(id);
    }
  }, [id]);

  const fetchPrompt = async (promptId: string) => {
    try {
      setLoading(true);

      // Fetch the prompt
      const { data: promptData, error: promptError } = await supabase
        .from("prompts")
        .select("*")
        .eq("id", promptId)
        .single();

      if (promptError) throw promptError;

      // Fetch the latest version
      const { data: versionData, error: versionError } = await supabase
        .from("prompt_versions")
        .select("*")
        .eq("prompt_id", promptId)
        .order("version_number", { ascending: false })
        .limit(1)
        .single();

      if (versionError) throw versionError;

      const promptWithVersion: PromptWithVersions = {
        ...promptData,
        versions: [versionData],
        latest_version: versionData,
      };

      setPrompt(promptWithVersion);
      setCurrentContent(versionData.content);

      // Initialize chat history with the current prompt
      setChatHistory([
        {
          role: "system",
          content:
            "I'm here to help you refine your VAPI-style prompt. Please provide feedback or instructions on how you'd like to improve it.",
        },
        {
          role: "assistant",
          content: `Here's your current prompt:\n\n${versionData.content}`,
        },
      ]);
    } catch (error) {
      console.error("Error fetching prompt:", error);
      toast({
        title: "Error",
        description: "Failed to load prompt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateImprovedPrompt = async () => {
    if (!prompt || !feedback.trim()) {
      toast({
        title: "Error",
        description: "Please provide feedback on how to improve the prompt.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGenerating(true);

      // Add user feedback to chat history
      const updatedHistory = [
        ...chatHistory,
        { role: "user", content: feedback },
      ];
      setChatHistory(updatedHistory);
      setFeedback("");

      // Get the session token for authorization
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Claude Supabase function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate_prompt_claude`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            conversationHistory: updatedHistory.map((msg) => ({
              role: msg.role === "system" ? "assistant" : msg.role,
              content: msg.content,
            })),
            instructions:
              "Refine the prompt based on the user's feedback. Return the complete updated prompt following VAPI guidelines.",
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to generate improved prompt",
        );
      }

      const data = await response.json();
      const refinedPrompt = data.prompt;

      // Update state with the generated content
      setGeneratedContent(refinedPrompt);
      setShowDiff(true);

      // Add AI response to chat history
      setChatHistory([
        ...updatedHistory,
        { role: "assistant", content: refinedPrompt },
      ]);
    } catch (error) {
      console.error("Error generating improved prompt:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to generate improved prompt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const acceptChanges = () => {
    setCurrentContent(generatedContent);
    setShowDiff(false);
    toast({
      title: "Changes Accepted",
      description:
        "The prompt has been updated with the AI-generated improvements.",
    });
  };

  const rejectChanges = () => {
    setShowDiff(false);
    toast({
      title: "Changes Rejected",
      description: "Reverted to the previous version of the prompt.",
    });

    // Update chat history to indicate rejection
    setChatHistory([
      ...chatHistory.slice(0, -1),
      {
        role: "system",
        content:
          "Changes were rejected. Please provide different feedback to improve the prompt.",
      },
    ]);
  };

  const saveChanges = async () => {
    if (!prompt || !currentContent.trim()) {
      toast({
        title: "Error",
        description: "Prompt content cannot be empty.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);

      // Get the current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Get the latest version number
      const { data: versionsData, error: versionsError } = await supabase
        .from("prompt_versions")
        .select("version_number")
        .eq("prompt_id", prompt.id)
        .order("version_number", { ascending: false })
        .limit(1);

      if (versionsError) throw versionsError;

      const nextVersionNumber =
        versionsData && versionsData.length > 0
          ? versionsData[0].version_number + 1
          : 1;

      // Create a new version
      const { error: versionError } = await supabase
        .from("prompt_versions")
        .insert({
          prompt_id: prompt.id,
          content: currentContent,
          version_number: nextVersionNumber,
          created_by: user.id,
        });

      if (versionError) throw versionError;

      // Update the prompt's updated_at timestamp
      const { error: promptError } = await supabase
        .from("prompts")
        .update({
          updated_at: new Date().toISOString(),
        })
        .eq("id", prompt.id);

      if (promptError) throw promptError;

      toast({
        title: "Success",
        description:
          "Prompt updated successfully with AI-assisted improvements.",
      });

      // Navigate back to the prompt detail page
      navigate(`/prompt-engine/prompt/${prompt.id}`);
    } catch (error) {
      console.error("Error saving prompt:", error);
      toast({
        title: "Error",
        description: "Failed to save prompt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p>Loading prompt...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!prompt) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">Prompt not found.</p>
            <Button className="mt-4" onClick={() => navigate("/prompt-engine")}>
              Back to Prompts
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center mb-2">
          <Button
            variant="ghost"
            size="sm"
            className="mr-2 -ml-2"
            onClick={() => navigate(`/prompt-engine/prompt/${prompt.id}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
        <CardTitle className="text-xl">AI-Assisted Prompt Editing</CardTitle>
        <CardDescription>
          Improve your prompt with AI assistance from Claude 3.7 Sonnet. Provide
          feedback and get AI-generated improvements.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-medium">Current Prompt</h3>
            <div className="text-xs text-muted-foreground">
              Version {prompt.latest_version?.version_number || 1}
            </div>
          </div>
          <ScrollArea className="border rounded-md p-4 h-[200px]">
            <pre className="whitespace-pre-wrap font-mono text-sm">
              {currentContent}
            </pre>
          </ScrollArea>
        </div>

        {showDiff ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium">
                Review AI-Generated Improvements
              </h3>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-good"
                  onClick={acceptChanges}
                >
                  <Check className="h-4 w-4 mr-1" /> Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-danger"
                  onClick={rejectChanges}
                >
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
              </div>
            </div>
            <div className="border rounded-md overflow-hidden">
              <InlineDiff
                oldText={currentContent}
                newText={generatedContent}
                onOldTextChange={(text) => setCurrentContent(text)}
                onNewTextChange={(text) => setGeneratedContent(text)}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">
              Provide Feedback for AI Improvement
            </h3>
            <div className="flex space-x-2">
              <Textarea
                placeholder="Describe how you'd like to improve this prompt (e.g., 'Make it more concise', 'Add more examples', 'Improve the constraints section')..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="flex-1 min-h-[100px]"
                disabled={isGenerating}
              />
              <Button
                onClick={generateImprovedPrompt}
                disabled={!feedback.trim()}
                isLoading={isGenerating}
                loadingText="Generating..."
                className="self-end"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Improve with AI
              </Button>
            </div>
          </div>
        )}

        {chatHistory.length > 2 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Conversation History</h3>
            <ScrollArea className="border rounded-md p-4 max-h-[300px]">
              <div className="space-y-4">
                {chatHistory.slice(2).map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${message.role === "user" ? "bg-primary text-primary-foreground" : message.role === "system" ? "bg-secondary text-secondary-foreground" : "bg-muted"}`}
                    >
                      <p className="whitespace-pre-wrap text-sm">
                        {message.content.length > 500
                          ? `${message.content.substring(0, 500)}... (full content in prompt above)`
                          : message.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate(`/prompt-engine/prompt/${prompt.id}`)}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <div className="space-x-2">
          <Button
            variant="default"
            onClick={saveChanges}
            disabled={currentContent === prompt.latest_version?.content}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            <Save className="mr-2 h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
